/**
 * Resources Data
 * Design templates, code references, and calculation guides
 */

export type ResourceCategory = 'template' | 'code_reference' | 'calculator' | 'guide';

export interface Resource {
  id: string;
  title: string;
  description: string;
  category: ResourceCategory;
  fileType: 'pdf' | 'xlsx' | 'docx' | 'txt';
  fileSize: string;
  lastUpdated: string;
  tags: string[];
  downloadCount?: number;
}

// Design Templates
export const designTemplates: Resource[] = [
  {
    id: 'template-voltage-drop',
    title: 'Voltage Drop Calculation Template',
    description: 'Excel template for calculating voltage drop in notification appliance circuits',
    category: 'template',
    fileType: 'xlsx',
    fileSize: '45 KB',
    lastUpdated: '2024-01-15',
    tags: ['voltage drop', 'NAC', 'calculations'],
    downloadCount: 1250
  },
  {
    id: 'template-battery-calc',
    title: 'Battery Calculation Worksheet',
    description: 'Comprehensive battery sizing worksheet per NFPA 72 requirements',
    category: 'template',
    fileType: 'xlsx',
    fileSize: '52 KB',
    lastUpdated: '2024-02-01',
    tags: ['battery', 'standby power', 'calculations'],
    downloadCount: 980
  },
  {
    id: 'template-device-schedule',
    title: 'Device Schedule Template',
    description: 'Standard device schedule format for fire alarm submittals',
    category: 'template',
    fileType: 'xlsx',
    fileSize: '38 KB',
    lastUpdated: '2024-01-20',
    tags: ['devices', 'schedule', 'submittal'],
    downloadCount: 2100
  },
  {
    id: 'template-inspection-checklist',
    title: 'Inspection Checklist',
    description: 'NFPA 72 compliant inspection and testing checklist',
    category: 'template',
    fileType: 'pdf',
    fileSize: '125 KB',
    lastUpdated: '2024-02-10',
    tags: ['inspection', 'testing', 'ITM'],
    downloadCount: 1560
  }
];

// Code References
export const codeReferences: Resource[] = [
  {
    id: 'ref-nfpa72-2022',
    title: 'NFPA 72 2022 Quick Reference',
    description: 'Key sections and requirements from NFPA 72 2022 edition',
    category: 'code_reference',
    fileType: 'pdf',
    fileSize: '2.1 MB',
    lastUpdated: '2024-01-01',
    tags: ['NFPA 72', '2022', 'code'],
    downloadCount: 3200
  },
  {
    id: 'ref-spacing-tables',
    title: 'Detector Spacing Tables',
    description: 'Smoke and heat detector spacing requirements per NFPA 72',
    category: 'code_reference',
    fileType: 'pdf',
    fileSize: '450 KB',
    lastUpdated: '2024-01-05',
    tags: ['spacing', 'detectors', 'tables'],
    downloadCount: 2800
  },
  {
    id: 'ref-candela-tables',
    title: 'Candela Rating Tables',
    description: 'Visual notification appliance candela requirements',
    category: 'code_reference',
    fileType: 'pdf',
    fileSize: '380 KB',
    lastUpdated: '2024-01-10',
    tags: ['candela', 'strobes', 'ADA'],
    downloadCount: 2150
  },
  {
    id: 'ref-wire-sizing',
    title: 'Wire Sizing Guide',
    description: 'Wire gauge selection and voltage drop guidelines',
    category: 'code_reference',
    fileType: 'pdf',
    fileSize: '290 KB',
    lastUpdated: '2024-02-05',
    tags: ['wire', 'gauge', 'voltage drop'],
    downloadCount: 1890
  }
];

// Calculation Guides
export const calculationGuides: Resource[] = [
  {
    id: 'guide-battery-sizing',
    title: 'Battery Sizing Guide',
    description: 'Step-by-step guide for calculating secondary power requirements',
    category: 'guide',
    fileType: 'pdf',
    fileSize: '1.2 MB',
    lastUpdated: '2024-01-25',
    tags: ['battery', 'calculations', 'tutorial'],
    downloadCount: 1450
  },
  {
    id: 'guide-voltage-drop',
    title: 'Voltage Drop Calculation Guide',
    description: 'Complete methodology for NAC voltage drop calculations',
    category: 'guide',
    fileType: 'pdf',
    fileSize: '980 KB',
    lastUpdated: '2024-02-01',
    tags: ['voltage drop', 'NAC', 'tutorial'],
    downloadCount: 1680
  },
  {
    id: 'guide-loop-loading',
    title: 'SLC Loop Loading Guide',
    description: 'Understanding addressable loop current calculations',
    category: 'guide',
    fileType: 'pdf',
    fileSize: '850 KB',
    lastUpdated: '2024-01-30',
    tags: ['SLC', 'addressable', 'loop'],
    downloadCount: 1120
  }
];

// All resources combined
export const allResources: Resource[] = [
  ...designTemplates,
  ...codeReferences,
  ...calculationGuides
];

/**
 * Search resources by query
 */
export function searchResources(query: string): Resource[] {
  const lowerQuery = query.toLowerCase();
  return allResources.filter(resource =>
    resource.title.toLowerCase().includes(lowerQuery) ||
    resource.description.toLowerCase().includes(lowerQuery) ||
    resource.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
  );
}

/**
 * Get resources by category
 */
export function getResourcesByCategory(category: ResourceCategory): Resource[] {
  return allResources.filter(resource => resource.category === category);
}

/**
 * Generate a mock file download (for demo purposes)
 */
export function generateResourceFile(resource: Resource): Blob {
  const content = `
${resource.title}
${'='.repeat(resource.title.length)}

Description: ${resource.description}
Category: ${resource.category}
Last Updated: ${resource.lastUpdated}
Tags: ${resource.tags.join(', ')}

This is a placeholder file for demonstration purposes.
In production, this would be the actual resource file.
  `.trim();
  
  return new Blob([content], { type: 'text/plain' });
}
